import JsonView from "@uiw/react-json-view"
import clsx from "clsx"
import { ComponentPropsWithoutRef, FC, Fragment, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { capitalize } from "@/helpers/capitalize"
import { useGame } from "@/providers/GameProvider"
import {
  IEffectAppliedLog,
  IGameplayLog,
  IStatChange
} from "@/store/apis/gameplayAPI"
import { useAppSelector } from "@/store/index"
import { AppSelectors } from "@/store/slices/app"
import { logsTypesOrder } from "@/ui/MatchLogs"
import { OutlinedText } from "@/ui/OutlinedText"
import {
  LogCardDiscarded,
  LogCardPlayed,
  LogDeckReshuffled,
  LogEffectActivated,
  LogEffectApplied,
  LogSkipRegistered
} from ".."
import styles from "./LogsList.module.scss"

interface ILogsList extends ComponentPropsWithoutRef<"div"> {
  data?: IGameplayLog[]
}

export const LogsList: FC<ILogsList> = ({ data, className, ...props }) => {
  const { t } = useTranslation()
  const { isMe, playerName, opponentName } = useGame()

  const isDevMode = useAppSelector(AppSelectors.selectIsDevMode())

  const getLogComponent = (log: IGameplayLog) => {
    switch (log.type) {
      case "EFFECT_ACTIVATED":
        return LogEffectActivated
      case "EFFECT_APPLIED":
        return LogEffectApplied
      case "CARD_PLAYED":
        return LogCardPlayed
      case "CARD_DISCARDED":
        return LogCardDiscarded
      case "DECK_RESHUFFLED":
        return LogDeckReshuffled
      case "SKIP_REGISTERED":
        return LogSkipRegistered
      default:
        return null
    }
  }

  const checkIsNegativeChange = (change?: IStatChange) => {
    if (!change) return false

    return change.before < change.after
  }

  const checkEffectHasNegativeChanges = (log: IEffectAppliedLog) => {
    const checkFreezingChanges = checkIsNegativeChange(
      log.details.changes?.freezingMovements
    )
    const checkSkipDiggingChanges = checkIsNegativeChange(
      log.details.changes?.skipDiggingMovements
    )
    const checkMirroringChanges = checkIsNegativeChange(
      log.details.changes?.mirroring
    )

    return (
      checkFreezingChanges || checkSkipDiggingChanges || checkMirroringChanges
    )
  }

  const logs = useMemo(() => {
    const logsGroupedByTurn = new Map<number, Map<string, IGameplayLog[]>>()

    data?.forEach((log) => {
      const turn = log.turn

      switch (log.type) {
        case "GAME_OVER":
        case "GAME_START": {
          return false
        }
      }

      if (!logsGroupedByTurn.has(turn)) {
        logsGroupedByTurn.set(turn, new Map<string, IGameplayLog[]>())
      }

      switch (log.type) {
        case "EFFECT_ACTIVATED": {
          if (log.details.duration <= 1 && !log.details.remainsActive) {
            return false
          }
          break
        }
        case "EFFECT_APPLIED": {
          const block = log.details?.changes?.block
          const progress = log.details?.changes?.progress

          if (log.details.effectType === "inc_block_distance") {
            if (!block) return false
          }

          if (log.details.effectType === "auto_dig") {
            if (!block && !progress) return false
          }

          if (checkEffectHasNegativeChanges(log)) {
            return false
          }

          break
        }
      }

      const logsType =
        log.type === "EFFECT_APPLIED" &&
        log.details.remainsActive &&
        !checkEffectHasNegativeChanges(log)
          ? isMe(log.details.targetPlayerId)
            ? "player"
            : "opponent"
          : "currentTurn"

      logsGroupedByTurn
        .get(turn)
        ?.set(logsType, [
          ...(logsGroupedByTurn.get(turn)?.get(logsType) || []),
          log
        ])
    })

    Array.from(logsGroupedByTurn.entries()).forEach(([turn, logs]) => {
      Array.from(logs.entries()).forEach(([player, logs]) => {
        let hasDischargeLog = false
        const filteredLogs = logs.filter((log) => {
          if (
            log.type === "EFFECT_APPLIED" &&
            log.details.effectType === "discharge"
          ) {
            if (hasDischargeLog) return false

            hasDischargeLog = true
          }

          return true
        })

        logsGroupedByTurn.get(turn)?.set(player, filteredLogs.reverse())
      })
    })

    return Array.from(logsGroupedByTurn.entries())
  }, [data])

  const logsList = useMemo(() => {
    return logs?.map(([turn, turnLogsRaw], i) => {
      const turnLogs = Array.from(turnLogsRaw.entries()).sort((a, b) => {
        return logsTypesOrder.indexOf(a[0]) - logsTypesOrder.indexOf(b[0])
      })

      const isMyTurn = isMe(turnLogs?.[0]?.[1]?.[0]?.currentPlayer)

      return (
        <div className={clsx(styles.LogsList_item)} key={i}>
          <span className={clsx(styles.LogsList_item_turn)}>
            {capitalize(t("turn_one"))} {turn + 1}{" "}
            <OutlinedText
              className={clsx(styles.LogsList_item_player, {
                [styles.me]: isMyTurn
              })}
            >
              {isMyTurn ? playerName : opponentName}
            </OutlinedText>
          </span>{" "}
          {turnLogs.map(([logsType, logs], j) => {
            const isCurrentTurn = logsType === "currentTurn"
            const isPlayerPrevTurns = logsType === "player"
            return (
              <Fragment key={j}>
                {j > 0 && (
                  <>
                    <br />
                  </>
                )}
                <div className={styles.LogsList_item_logs}>
                  {!isCurrentTurn && (
                    <>
                      <OutlinedText
                        className={clsx(styles.LogsList_item_player, {
                          [styles.me]: isPlayerPrevTurns
                        })}
                      >
                        {isPlayerPrevTurns ? playerName : opponentName}
                      </OutlinedText>
                      :{" "}
                    </>
                  )}
                  {logs.map((log, k) => {
                    const LogComponent = getLogComponent(log)

                    if (!LogComponent) return null

                    return (
                      <Fragment key={k}>
                        {k > 0 && "; "}
                        <LogComponent
                          {...(log as any)}
                          isCurrentTurn={isCurrentTurn}
                        />
                      </Fragment>
                    )
                  })}
                </div>
                {isDevMode && <JsonView value={logs} collapsed={true} />}
              </Fragment>
            )
          })}
        </div>
      )
    })
  }, [logs, playerName, opponentName])

  return (
    <div {...props} className={clsx(styles.LogsList, className)}>
      {logsList.length === 0 && (
        <div className={styles.LogsList_empty}>{t("logs.empty")}</div>
      )}
      {logsList}
    </div>
  )
}
